import { Backdrop, CircularProgress, MenuItem, Stack, Typography } from '@mui/material'
import ChecklistRoundedIcon from '@mui/icons-material/ChecklistRounded'
import { useEffect, useRef } from 'react'
import { toast } from 'sonner'
import CriteriaCheckDialog from '@features/subject/components/CriteriaCheckDialog'
import {
  ContainerAdherenceChecksParams,
  useGetContainerAdherenceChecks,
} from '@features/subject/hooks/useGetContainerAdherenceChecks'
import { SPACING } from '@common/theme/spacing'

type CriteriaCheckResultsMenuItemProps = {
  onClick: () => void
}

type CriteriaCheckResultsDialogProps = {
  open: boolean
  onClose: () => void
  fetchParams: ContainerAdherenceChecksParams
}

/** Menu row for opening criteria check results (place inside MenuList). */
export function CriteriaCheckResultsMenuItem({ onClick }: CriteriaCheckResultsMenuItemProps) {
  return (
    <MenuItem
      sx={{
        height: 36,
        width: '100%',
        paddingY: SPACING.examDetailPopupMenuListSpacing,
      }}
      onClick={onClick}
      data-testid="popup-menu-list-item-criteria-check-results"
    >
      <Stack direction="row" spacing={SPACING.popupMenuIconSpacing} alignItems="center">
        <ChecklistRoundedIcon
          sx={{
            color: 'text.secondary',
          }}
        />
        <Typography>Criteria check results</Typography>
      </Stack>
    </MenuItem>
  )
}

/**
 * Loads persisted checks for a container and renders the existing CriteriaCheckDialog.
 * Place outside the Menu (same pattern as UploaderDetailsModal).
 */
export function CriteriaCheckResultsDialog({ open, onClose, fetchParams }: CriteriaCheckResultsDialogProps) {
  const { checks, isLoading, isError } = useGetContainerAdherenceChecks(fetchParams, open)
  const handledErrorRef = useRef(false)

  useEffect(() => {
    if (!open) {
      handledErrorRef.current = false
      return
    }
    if (isLoading || !isError || handledErrorRef.current) {
      return
    }
    handledErrorRef.current = true
    toast.error('Failed to load criteria check results')
    onClose()
  }, [open, isLoading, isError, onClose])

  if (!open) {
    return null
  }

  return (
    <>
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }} open={isLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>
      {!isLoading && !isError && <CriteriaCheckDialog checks={checks} onClose={onClose} />}
    </>
  )
}

export default CriteriaCheckResultsMenuItem
